import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { isMainList, isTomorrowList, isDailyHabitsList } from '../constants/listTypes';
import ConfirmModal from '../components/ConfirmModal';

export default function RecurringTasks({ lists }) {
  const [recurringTasks, setRecurringTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showRecurringForm, setShowRecurringForm] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, job: null });
  const [recurringFormData, setRecurringFormData] = useState({
    tasks: [{ text: '', sub_tasks: [], notes: [] }],
    targetListId: '',
    taskName: '',
    frequency: 'daily', // 'daily', 'weekly', 'monthly', 'interval'
    days: [], // For weekly: [0-6] (Sunday-Saturday), For monthly: [1-31]
    businessDaysOnly: false,
    alsoAddToSpecialList: 'none', // 'none', 'main', 'tomorrow'
    dayInterval: null, // For interval: number of days between recurrences
    weekendHandling: 'after' // 'after' (Monday) or 'before' (Friday) for weekend occurrences
  });
  const [newSubTaskTexts, setNewSubTaskTexts] = useState({}); // { taskIndex: text }
  const [newNoteTexts, setNewNoteTexts] = useState({}); // { taskIndex: text }

  // Load recurring tasks from API
  const loadRecurringTasks = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await api.getRecurringTasks();
      // Transform API response - tasks already have sub_tasks and notes
      setRecurringTasks(data);
    } catch (error) {
      console.error('Error loading recurring tasks:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecurringTasks();
  }, [loadRecurringTasks]);

  // Recurring job handlers
  const handleAddRecurringTask = () => {
    setRecurringFormData({
      ...recurringFormData,
      tasks: [...recurringFormData.tasks, { text: '', sub_tasks: [], notes: [] }]
    });
  };

  const handleRecurringTaskChange = (index, value) => {
    const newTasks = [...recurringFormData.tasks];
    newTasks[index] = { ...newTasks[index], text: value };
    setRecurringFormData({
      ...recurringFormData,
      tasks: newTasks
    });
  };

  const handleRemoveRecurringTask = (index) => {
    const newTasks = recurringFormData.tasks.filter((_, i) => i !== index);
    setRecurringFormData({
      ...recurringFormData,
      tasks: newTasks.length > 0 ? newTasks : [{ text: '', sub_tasks: [], notes: [] }]
    });
  };

  const handleAddSubTask = (taskIndex) => {
    const text = newSubTaskTexts[taskIndex] || '';
    if (text.trim()) {
      const newTasks = [...recurringFormData.tasks];
      newTasks[taskIndex] = {
        ...newTasks[taskIndex],
        sub_tasks: [...(newTasks[taskIndex].sub_tasks || []), text.trim()]
      };
      setRecurringFormData({
        ...recurringFormData,
        tasks: newTasks
      });
      // Clear the input
      setNewSubTaskTexts({
        ...newSubTaskTexts,
        [taskIndex]: ''
      });
    }
  };

  const handleSubTaskKeyDown = (e, taskIndex) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddSubTask(taskIndex);
    }
  };

  const handleRemoveSubTask = (taskIndex, subTaskIndex) => {
    const newTasks = [...recurringFormData.tasks];
    newTasks[taskIndex] = {
      ...newTasks[taskIndex],
      sub_tasks: newTasks[taskIndex].sub_tasks.filter((_, i) => i !== subTaskIndex)
    };
    setRecurringFormData({
      ...recurringFormData,
      tasks: newTasks
    });
  };

  const handleAddNote = (taskIndex) => {
    const text = newNoteTexts[taskIndex] || '';
    if (text.trim()) {
      const newTasks = [...recurringFormData.tasks];
      newTasks[taskIndex] = {
        ...newTasks[taskIndex],
        notes: [...(newTasks[taskIndex].notes || []), text.trim()]
      };
      setRecurringFormData({
        ...recurringFormData,
        tasks: newTasks
      });
      // Clear the input
      setNewNoteTexts({
        ...newNoteTexts,
        [taskIndex]: ''
      });
    }
  };

  const handleNoteKeyDown = (e, taskIndex) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddNote(taskIndex);
    }
  };

  const handleRemoveNote = (taskIndex, noteIndex) => {
    const newTasks = [...recurringFormData.tasks];
    newTasks[taskIndex] = {
      ...newTasks[taskIndex],
      notes: newTasks[taskIndex].notes.filter((_, i) => i !== noteIndex)
    };
    setRecurringFormData({
      ...recurringFormData,
      tasks: newTasks
    });
  };

  const handleToggleRecurringDay = (day) => {
    const currentDays = recurringFormData.days || [];
    const newDays = currentDays.includes(day)
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day].sort((a, b) => a - b);
    
    setRecurringFormData({
      ...recurringFormData,
      days: newDays
    });
  };

  const handleCreateRecurringTask = async (e) => {
    e.preventDefault();
      const validTasks = recurringFormData.tasks.filter(task => task.text && task.text.trim());
    
    if (!recurringFormData.taskName.trim() || !recurringFormData.targetListId || validTasks.length === 0) {
      return;
    }

    // Validate days selection for weekly/monthly
    if (recurringFormData.frequency === 'weekly' && recurringFormData.days.length === 0) {
      alert('Please select at least one day for weekly recurrence.');
      return;
    }

    if (recurringFormData.frequency === 'monthly' && recurringFormData.days.length === 0) {
      alert('Please select at least one day of the month for monthly recurrence.');
      return;
    }

    // Validate day interval for interval frequency
    if (recurringFormData.frequency === 'interval' && (!recurringFormData.dayInterval || recurringFormData.dayInterval < 1)) {
      alert('Please enter a valid number of days (at least 1) for interval recurrence.');
      return;
    }

    try {
      // Convert tasks to RecurringJobTaskCreate format
      const tasks = validTasks.map(task => ({
        text: task.text,
        sub_tasks: task.sub_tasks || [],
        notes: task.notes || []
      }));
      
      const newJob = await api.createRecurringTask({
        task_name: recurringFormData.taskName.trim(),
        target_list_id: recurringFormData.targetListId,
        frequency: recurringFormData.frequency,
        days: [...recurringFormData.days],
        business_days_only: recurringFormData.businessDaysOnly,
        also_add_to_special_list: recurringFormData.alsoAddToSpecialList,
        day_interval: recurringFormData.dayInterval,
        weekend_handling: recurringFormData.weekendHandling,
        tasks: tasks
      });

      setRecurringTasks([...recurringTasks, newJob]);
      setRecurringFormData({
        tasks: [{ text: '', sub_tasks: [], notes: [] }],
        targetListId: '',
        taskName: '',
        frequency: 'daily',
        days: [],
        businessDaysOnly: false,
        alsoAddToSpecialList: 'none',
        dayInterval: null,
        weekendHandling: 'after'
      });
      setShowRecurringForm(false);
    } catch (error) {
      console.error('Error creating recurring task job:', error);
      alert('Failed to create recurring task job. Please try again.');
    }
  };

  const handleDeleteClick = (job) => {
    setDeleteModal({ isOpen: true, job });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.job) return;

    try {
      await api.deleteRecurringTask(deleteModal.job.id);
      setRecurringTasks(recurringTasks.filter(task => task.id !== deleteModal.job.id));
      setDeleteModal({ isOpen: false, job: null });
    } catch (error) {
      console.error('Error deleting recurring task job:', error);
      alert('Failed to delete recurring task job. Please try again.');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, job: null });
  };

  return (
    <>
      {!showRecurringForm ? (
        <div>
          <button
            className="btn-custom btn-primary-custom"
            onClick={() => setShowRecurringForm(true)}
            style={{ marginBottom: '1.5rem' }}
          >
            + Create Recurring Task Job
          </button>

          {isLoading ? (
            <div className="empty-state">
              <div className="empty-state-icon">⏳</div>
              <p className="empty-state-text">Loading recurring tasks...</p>
            </div>
          ) : recurringTasks.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🔄</div>
              <p className="empty-state-text">No recurring task jobs yet</p>
              <p className="empty-state-text" style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                Create a job to automatically add tasks on a schedule
              </p>
            </div>
          ) : (
            <div className="recurring-tasks-list">
              {recurringTasks.map(task => (
                <div key={task.id} className="recurring-task-item">
                  <div className="recurring-task-header">
                    <div className="recurring-task-info">
                      <h3 className="recurring-task-name">
                        <span className="recurring-icon">🔄</span>
                        {task.taskName}
                      </h3>
                      <div className="recurring-task-meta">
                        <span className="recurring-task-target">
                          Adds to: <strong>{task.targetListName}</strong>
                          {task.alsoAddToSpecialList && task.alsoAddToSpecialList !== 'none' && (
                            <span> + {task.alsoAddToSpecialList === 'main' ? 'To Do Today' : 'To Do Tomorrow'}</span>
                          )}
                        </span>
                        <span className="recurring-task-tasks-count">
                          {task.tasks.length} {task.tasks.length === 1 ? 'task' : 'tasks'}
                        </span>
                        <span className="recurring-task-frequency">
                          {task.frequency === 'daily' && 'Daily'}
                          {task.frequency === 'weekly' && `Weekly: ${task.days.map(d => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d]).join(', ')}`}
                          {task.frequency === 'monthly' && `Monthly: Day ${task.days.join(', ')}`}
                          {task.frequency === 'interval' && `Every ${task.dayInterval} ${task.dayInterval === 1 ? 'day' : 'days'}`}
                        </span>
                        {task.businessDaysOnly && (
                          <span className="recurring-task-business-days">
                            Business days only ({task.weekendHandling === 'before' ? 'Friday' : 'Monday'} for weekends)
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="recurring-task-actions">
                      {(() => {
                        // Find the target list to check if it's a Daily Habits list
                        const targetList = lists.find(l => l.id === task.targetListId);
                        const isDailyHabits = targetList && isDailyHabitsList(targetList.list_type);
                        
                        // Hide delete button for Daily Habits recurring jobs
                        if (isDailyHabits) {
                          return null;
                        }
                        
                        return (
                          <button
                            className="btn-delete-list btn-delete-list-no-tooltip"
                            onClick={() => handleDeleteClick(task)}
                            aria-label={`Delete recurring task job ${task.taskName}`}
                          >
                            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M3 6h14M8 6V4a2 2 0 0 1 2-2h0a2 2 0 0 1 2 2v2m3 0v10a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14zM10 11v6M7 11v6M13 11v6" />
                            </svg>
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="recurring-task-tasks">
                    <ul className="recurring-job-tasks-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {task.tasks && task.tasks.map((taskItem, idx) => (
                        <li key={idx} style={{ marginBottom: '1rem' }}>
                          <div className="recurring-task-text" style={{ marginBottom: '0.5rem' }}>
                            {taskItem.text}
                          </div>
                          {taskItem.sub_tasks && taskItem.sub_tasks.length > 0 && (
                            <div style={{ marginLeft: '1rem', marginBottom: '0.5rem' }}>
                              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Sub-tasks:</div>
                              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                {taskItem.sub_tasks.map((subTask, subIdx) => (
                                  <li key={subIdx} style={{ fontSize: '0.875rem', color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
                                    {subTask.text}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {taskItem.notes && taskItem.notes.length > 0 && (
                            <div style={{ marginLeft: '1rem' }}>
                              <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>Notes:</div>
                              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                {taskItem.notes.map((note, noteIdx) => (
                                  <li key={noteIdx} style={{ fontSize: '0.875rem', color: 'var(--text-primary)', marginBottom: '0.25rem', whiteSpace: 'pre-wrap' }}>
                                    {note.text}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="recurring-form-container">
          <div className="form-header">
            <h3 className="form-header-title">Create Recurring Task Job</h3>
            <button
              className="btn-icon"
              onClick={() => {
                setShowRecurringForm(false);
                setRecurringFormData({
                  tasks: [{ text: '', sub_tasks: [], notes: [] }],
                  targetListId: '',
                  taskName: '',
                  frequency: 'daily',
                  days: [],
                  businessDaysOnly: false,
                  alsoAddToSpecialList: 'none',
                  dayInterval: null,
                  weekendHandling: 'after'
                });
                setNewSubTaskTexts({});
                setNewNoteTexts({});
              }}
              aria-label="Cancel"
              title="Cancel"
            >
              ×
            </button>
          </div>
          <form onSubmit={handleCreateRecurringTask} className="form-body">
            <div style={{ marginBottom: '1rem' }}>
              <label className="form-label">Job Name</label>
              <input
                type="text"
                value={recurringFormData.taskName}
                onChange={(e) => setRecurringFormData({ ...recurringFormData, taskName: e.target.value })}
                placeholder="e.g., Weekly Review, Daily Standup"
                className="input-custom"
                style={{ width: '100%' }}
                required
                aria-label="Job name"
              />
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem', marginBottom: 0 }}>
                A friendly name for this recurring task job
              </p>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label className="form-label">Frequency</label>
              <select
                value={recurringFormData.frequency}
                onChange={(e) => setRecurringFormData({ ...recurringFormData, frequency: e.target.value, days: [], dayInterval: null })}
                className="input-custom"
                style={{ width: '100%' }}
                required
                aria-label="Frequency"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="interval">Every N Days</option>
              </select>
            </div>

            {recurringFormData.frequency === 'interval' && (
              <div style={{ marginBottom: '1rem' }}>
                <label className="form-label">Repeat Every (Days)</label>
                <input
                  type="number"
                  min="1"
                  value={recurringFormData.dayInterval || ''}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, dayInterval: parseInt(e.target.value) || null })}
                  placeholder="e.g., 3 for every 3 days"
                  className="input-custom"
                  style={{ width: '100%' }}
                  required
                  aria-label="Day interval"
                />
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem', marginBottom: 0 }}>
                  Number of days between task recurrences
                </p>
              </div>
            )}

            {recurringFormData.frequency === 'weekly' && (
              <div style={{ marginBottom: '1rem' }}>
                <label className="form-label">Days of Week</label>
                <div className="days-selector">
                  {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => (
                    <button
                      key={index}
                      type="button"
                      className={`day-button ${recurringFormData.days.includes(index) ? 'day-button-selected' : ''}`}
                      onClick={() => handleToggleRecurringDay(index)}
                    >
                      {day.substring(0, 3)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {recurringFormData.frequency === 'monthly' && (
              <div style={{ marginBottom: '1rem' }}>
                <label className="form-label">Day(s) of Month</label>
                <div className="days-selector">
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                    <button
                      key={day}
                      type="button"
                      className={`day-button ${recurringFormData.days.includes(day) ? 'day-button-selected' : ''}`}
                      onClick={() => handleToggleRecurringDay(day)}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginBottom: '1rem' }}>
              <label className="form-label-checkbox">
                <input
                  type="checkbox"
                  checked={recurringFormData.businessDaysOnly}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, businessDaysOnly: e.target.checked })}
                  style={{ marginRight: '0.5rem' }}
                />
                Only add on business days (skip weekends)
              </label>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem', marginBottom: 0 }}>
                If the scheduled date falls on a weekend, tasks will be moved to a business day
              </p>
            </div>

            {recurringFormData.businessDaysOnly && (
              <div style={{ marginBottom: '1rem', marginLeft: '1.5rem' }}>
                <label className="form-label">Weekend Handling</label>
                <select
                  value={recurringFormData.weekendHandling}
                  onChange={(e) => setRecurringFormData({ ...recurringFormData, weekendHandling: e.target.value })}
                  className="input-custom"
                  style={{ width: '100%' }}
                  aria-label="Weekend handling"
                >
                  <option value="after">Add on next business day (Monday)</option>
                  <option value="before">Add on previous business day (Friday)</option>
                </select>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem', marginBottom: 0 }}>
                  Choose whether to add weekend tasks before or after the weekend
                </p>
              </div>
            )}

            <div style={{ marginBottom: '1rem' }}>
              <label className="form-label">Add Tasks To</label>
              <select
                value={recurringFormData.targetListId}
                onChange={(e) => setRecurringFormData({ ...recurringFormData, targetListId: e.target.value })}
                className="input-custom"
                style={{ width: '100%' }}
                required
                aria-label="Target list"
              >
                <option value="">Select a list...</option>
                {lists
                  .filter(list => !isMainList(list.list_type) && !isTomorrowList(list.list_type))
                  .map(list => (
                    <option key={list.id} value={list.id}>
                      {list.name}
                    </option>
                  ))}
              </select>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem', marginBottom: 0 }}>
                Select a regular list where tasks will be added (not To Do Today or To Do Tomorrow)
              </p>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label className="form-label">Also Add To Special List</label>
              <select
                value={recurringFormData.alsoAddToSpecialList}
                onChange={(e) => setRecurringFormData({ ...recurringFormData, alsoAddToSpecialList: e.target.value })}
                className="input-custom"
                style={{ width: '100%' }}
                aria-label="Also add to special list"
              >
                <option value="none">None</option>
                <option value="main">To Do Today</option>
                <option value="tomorrow">To Do Tomorrow</option>
              </select>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem', marginBottom: 0 }}>
                Optionally also add tasks to a special list when they are created
              </p>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label className="form-label">Tasks to Create</label>
              {recurringFormData.tasks.map((task, index) => (
                <div key={index} style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', background: 'var(--bg-secondary)' }}>
                  <div className="recurring-task-input-group" style={{ marginBottom: '0.75rem' }}>
                    <input
                      type="text"
                      value={task.text}
                      onChange={(e) => handleRecurringTaskChange(index, e.target.value)}
                      placeholder="Enter task text..."
                      className="input-custom"
                      style={{ flex: 1, marginRight: '0.5rem' }}
                      aria-label={`Task ${index + 1}`}
                    />
                    {recurringFormData.tasks.length > 1 && (
                      <button
                        type="button"
                        className="btn-icon btn-delete"
                        onClick={() => handleRemoveRecurringTask(index)}
                        aria-label={`Remove task ${index + 1}`}
                      >
                        ×
                      </button>
                    )}
                  </div>
                  
                  {/* Sub-tasks */}
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem', display: 'block' }}>Sub-tasks</label>
                    {task.sub_tasks && task.sub_tasks.length > 0 && (
                      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 0.5rem 0' }}>
                        {task.sub_tasks.map((subTask, subIndex) => (
                          <li key={subIndex} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem', padding: '0.25rem 0.5rem', background: 'var(--bg-card)', borderRadius: 'var(--radius-sm)' }}>
                            <span style={{ flex: 1, fontSize: '0.875rem' }}>{subTask}</span>
                            <button
                              type="button"
                              className="btn-icon btn-delete"
                              onClick={() => handleRemoveSubTask(index, subIndex)}
                              aria-label={`Remove sub-task ${subIndex + 1}`}
                              style={{ fontSize: '0.875rem' }}
                            >
                              ×
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input
                        type="text"
                        value={newSubTaskTexts[index] || ''}
                        onChange={(e) => setNewSubTaskTexts({ ...newSubTaskTexts, [index]: e.target.value })}
                        onKeyDown={(e) => handleSubTaskKeyDown(e, index)}
                        className="input-custom"
                        placeholder="Add a sub-task..."
                        style={{ flex: 1, fontSize: '0.875rem', padding: '0.375rem 0.75rem' }}
                      />
                      <button
                        type="button"
                        className="btn-custom btn-secondary-custom"
                        onClick={() => handleAddSubTask(index)}
                        disabled={!newSubTaskTexts[index] || !newSubTaskTexts[index].trim()}
                        style={{ fontSize: '0.875rem', padding: '0.375rem 0.75rem', whiteSpace: 'nowrap' }}
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label style={{ fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem', display: 'block' }}>Notes</label>
                    {task.notes && task.notes.length > 0 && (
                      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 0.5rem 0' }}>
                        {task.notes.map((note, noteIndex) => (
                          <li key={noteIndex} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.25rem', padding: '0.5rem', background: 'var(--bg-card)', borderRadius: 'var(--radius-sm)' }}>
                            <span style={{ flex: 1, fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>{note}</span>
                            <button
                              type="button"
                              className="btn-icon btn-delete"
                              onClick={() => handleRemoveNote(index, noteIndex)}
                              aria-label={`Remove note ${noteIndex + 1}`}
                              style={{ fontSize: '0.875rem' }}
                            >
                              ×
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
                      <textarea
                        value={newNoteTexts[index] || ''}
                        onChange={(e) => setNewNoteTexts({ ...newNoteTexts, [index]: e.target.value })}
                        onKeyDown={(e) => handleNoteKeyDown(e, index)}
                        className="input-custom"
                        placeholder="Add a note..."
                        style={{ 
                          fontSize: '0.875rem', 
                          padding: '0.5rem 0.75rem',
                          minHeight: '60px',
                          resize: 'vertical',
                          fontFamily: 'inherit'
                        }}
                        rows={2}
                      />
                      <button
                        type="button"
                        className="btn-custom btn-secondary-custom"
                        onClick={() => handleAddNote(index)}
                        disabled={!newNoteTexts[index] || !newNoteTexts[index].trim()}
                        style={{ fontSize: '0.875rem', padding: '0.375rem 0.75rem', alignSelf: 'flex-start' }}
                      >
                        Add Note
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="btn-custom btn-secondary-custom"
                onClick={handleAddRecurringTask}
                style={{ marginTop: '0.5rem' }}
              >
                + Add Another Task
              </button>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem', marginBottom: 0 }}>
                These tasks will be created automatically when the job runs, including their sub-tasks and notes
              </p>
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="btn-custom btn-primary-custom"
                style={{ flex: 1 }}
                disabled={!recurringFormData.taskName.trim() || !recurringFormData.targetListId || recurringFormData.tasks.filter(t => t.text && t.text.trim()).length === 0}
              >
                Create Recurring Task Job
              </button>
              <button
                type="button"
                className="btn-custom btn-secondary-custom"
                onClick={() => {
                  setShowRecurringForm(false);
                  setRecurringFormData({
                    tasks: [{ text: '', sub_tasks: [], notes: [] }],
                    targetListId: '',
                    taskName: '',
                    frequency: 'daily',
                    days: [],
                    businessDaysOnly: false,
                    alsoAddToSpecialList: 'none',
                    dayInterval: null,
                    weekendHandling: 'after'
                  });
                  setNewSubTaskTexts({});
                  setNewNoteTexts({});
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        title="Delete Recurring Task Job"
        message={`Are you sure you want to delete "${deleteModal.job?.taskName}"? This will permanently delete the recurring task job and it will no longer create tasks automatically.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isDanger={true}
      />
    </>
  );
}

